import { convertQuestionAnswersToTextVersionForGPT, getMessagesForAiSummary } from '@arnold/core';
import { encodeChat } from 'gpt-tokenizer';
import type { ChatMessage } from 'gpt-tokenizer/esm/GptEncoding';
import type { QuestionWithAnswer } from '../../generated/hooks';

// Shared with legacy reports: keep this prompt unchanged, including its whitespace, for cache compatibility.
export const SURVEY_SUMMARY_PROMPT = `As an research assistant for Arnold, your role is to distill valuable insights from user feedback in surveys created by our clients (companies).
Your primary task is to provide a summary of the survey results below. Your summary output should at all times be in language %LANGUAGE_CODE% no matter what the language of the survey is.
Key guidelines to follow:
1. Summary should be in bullet points
2. Summary should include the most important insights from the survey.
3. The questions have their id in [square brackets] at the end of the question.
4. If a bullet point is specific to some questions, please add question ids separated by commas in square brackets at the end of the bullet point like this [5, 34]
5. It should be short, around 8 points.
6. If there is not enough data, feel free to enter fewer point.
7. Pay attention to answers in FREETEXT, these are usually the most important.
8. Add also a sentiment in [square brackets] at the beginning of each point. Allowed sentiment types: [NEGATIVE], [POSITIVE], [NEUTRAL].
9. Arrange the sentiment in this order [NEGATIVE], [POSITIVE], [NEUTRAL]
10. Sentiments in square brackets should not be translated. They should be in english as defined in the guidelines. Do not make a new line after the sentiment.
`;

/** Deterministic sampling of free text only. Choice histograms are never shortened. */
export function prepareSummaryAnswers(
  answers: QuestionWithAnswer[],
  languageCode: string,
  maxTokens = (Number(process.env.CHATGPT_REPORT_SUMMARY_MAX_TOKENS_K) || 70) * 1000,
): QuestionWithAnswer[] {
  let selected = answers.map((question) => ({
    ...question,
    ...(question.answers && {
      answers: question.answers
        .filter((answer) => answer.length > 4)
        .slice()
        .sort((a, b) => a.length - b.length || a.localeCompare(b)),
    }),
  }));
  const tokenCount = (input: QuestionWithAnswer[]) =>
    encodeChat(
      getMessagesForAiSummary(
        convertQuestionAnswersToTextVersionForGPT(input),
        SURVEY_SUMMARY_PROMPT,
        languageCode,
      ) as ChatMessage[],
      (process.env.CHATGPT_REPORT_SUMMARY_MODEL as any) || 'gpt-4o',
    ).length;
  let tokens = tokenCount(selected);
  while (tokens > maxTokens) {
    // Always remove at least one answer per nonempty text entry so this also terminates for choice-only input.
    const ratio = Math.min(0.9, maxTokens / tokens);
    let removed = false;
    selected = selected.map((question) => {
      if (!question.answers?.length) return question;
      removed = true;
      const count = Math.min(question.answers.length - 1, Math.floor(question.answers.length * ratio));
      return {
        ...question,
        answers: Array.from(
          { length: count },
          (_, index) => question.answers![Math.floor((index * question.answers!.length) / count)],
        ),
      };
    });
    if (!removed) break;
    tokens = tokenCount(selected);
  }
  return selected.filter((question) => question.answers == null || question.answers.length > 0);
}
